// src/hooks/useAuth.js
import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { auth } from "../firebase";

const INACTIVIDAD_MINUTOS = 30;
const INACTIVIDAD_MS      = INACTIVIDAD_MINUTOS * 60 * 1000;

// ── Contexto único — una sola instancia en toda la app ────────────────────────
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  const logout = useCallback(() => signOut(auth), []);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      signOut(auth);
      alert(`⏱ Sesión cerrada por inactividad (${INACTIVIDAD_MINUTOS} min). Volvé a iniciar sesión.`);
    }, INACTIVIDAD_MS);
  }, []);

  // Escucha de auth — solo actualiza si el uid realmente cambia
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(prev => {
        if (prev?.uid === u?.uid) return prev;
        return u;
      });
      setLoading(false);
      if (u) resetTimer();
      else if (timerRef.current) clearTimeout(timerRef.current);
    });
    return unsub;
  }, [resetTimer]);

  // Event listeners de actividad — registrados UNA sola vez aquí
  useEffect(() => {
    if (!user) return;
    const eventos = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"];
    eventos.forEach(ev => window.addEventListener(ev, resetTimer, { passive: true, capture: true }));
    return () => {
      eventos.forEach(ev => window.removeEventListener(ev, resetTimer, { capture: true }));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [user, resetTimer]);

  const login = (email, password) => signInWithEmailAndPassword(auth, email, password);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook para consumir el contexto — todos los componentes usan la misma instancia
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro de <AuthProvider>");
  return ctx;
}
